import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal, ModalBody, ModalContent, ModalHeader, Spinner, Tooltip } from "@nextui-org/react";
import { Icon } from "@iconify/react";
import { CtyButton } from "../button/CtyButton";
import { CtyShowToast, checkType } from "../toast/CtyToast";
import { useHasApplicationResource } from "../utiluicomponents/useCheckApplicationResource";
import { useGetAdvisorKnowledgeMetadataQuery } from '@circuitry-ai/doc-data';

const formatMetadataValue = (value: unknown): string => {
    if (Array.isArray(value)) return value.filter(Boolean).join(', ');
    if (value === null || value === undefined || value === '') return '-';
    return String(value);
};

export interface MetadataInfoButtonProps {
    sourceId: string;
    id?: string;
}

/**
 * Reusable info icon + read-only metadata modal. Reused across PDF, DITA HTML,
 * DITA ZIP (topic viewer) and the parts catalog (BOM) viewers so metadata display
 * stays consistent and centrally gated behind the `knowledge_metadata` resource flag.
 */
export function MetadataInfoButton({ sourceId, id = 'file-viewer-metadata-info' }: MetadataInfoButtonProps) {
    const { t } = useTranslation();
    const hasKnowledgeMetadata = useHasApplicationResource('knowledge_metadata');
    const [isMetadataModalOpen, setIsMetadataModalOpen] = useState(false);

    const { data: metadataResponse, isFetching: isFetchingMetadata, isError: isMetadataError } =
        useGetAdvisorKnowledgeMetadataQuery(
            { advisor_id: sourceId },
            { skip: !sourceId || !isMetadataModalOpen, refetchOnMountOrArgChange: true }
        );
    const metadataItems = useMemo(() => metadataResponse?.data ?? [], [metadataResponse?.data]);
    const securityLevel = useMemo(
        () => formatMetadataValue(metadataItems.find((item) => item.name?.toLowerCase() === 'security')?.value),
        [metadataItems]
    );
    const attributeItems = useMemo(
        () => metadataItems.filter((item) => item.name?.toLowerCase() !== 'security'),
        [metadataItems]
    );

    if (!hasKnowledgeMetadata) return null;

    const handleOpenMetadata = () => {
        if (!sourceId) {
            CtyShowToast(checkType.error, t('Unable to fetch metadata'));
            return;
        }
        setIsMetadataModalOpen(true);
    };

    return (
        <>
            <Tooltip content={t('View metadata')} placement="top" showArrow>
                <span>
                    <CtyButton
                        id={id}
                        isIconOnly
                        variant="light"
                        size="sm"
                        color="primary"
                        onPress={handleOpenMetadata}
                        aria-label={t('View metadata')}
                    >
                        <Icon icon="mdi:information-outline" className="h-5 w-5 text-primary" color="primary" />
                    </CtyButton>
                </span>
            </Tooltip>
            <Modal
                isOpen={isMetadataModalOpen}
                onOpenChange={setIsMetadataModalOpen}
                size="3xl"
                scrollBehavior="inside"
                className="max-h-[90vh]"
            >
                <ModalContent>
                    {() => (
                        <>
                            <ModalHeader className="flex flex-col gap-1">
                                <div>{t('Knowledge Metadata')}</div>
                                <div className="text-sm font-normal text-default-500">
                                    {t('View metadata attributes for this file')}
                                </div>
                            </ModalHeader>
                            <ModalBody className="overflow-y-auto">
                                <div className="flex gap-3 flex-col">
                                    {isFetchingMetadata && (
                                        <div className="flex items-center gap-2 text-sm text-default-500">
                                            <Spinner size="sm" />
                                            <span>{t('Loading metadata...')}</span>
                                        </div>
                                    )}
                                    {!isFetchingMetadata && isMetadataError && (
                                        <p className="text-sm text-red-500">{t('Unable to fetch metadata')}</p>
                                    )}
                                    {!isFetchingMetadata && !isMetadataError && (
                                        <>
                                            <div className="flex flex-row items-center justify-between gap-2">
                                                <label className="text-sm w-[100%]">{t('Security Level')}</label>
                                                <div className="w-full rounded-xl bg-default-100 px-4 py-2.5 text-sm text-default-700">
                                                    {securityLevel}
                                                </div>
                                            </div>
                                            <label className="text-sm w-[100%]">{t('Attributes')}</label>
                                            {attributeItems.length === 0 && (
                                                <p className="text-sm text-default-500">{t('No metadata available')}</p>
                                            )}
                                            {attributeItems.map((item) => (
                                                <div key={item.id} className="flex flex-row items-start justify-start gap-2">
                                                    <div className="flex-1">
                                                        <label className="text-sm">{t('Attribute Name')}</label>
                                                        <div className="mt-1 rounded-xl bg-default-100 px-4 py-2.5 text-sm text-default-700 break-words">
                                                            {item.name || '-'}
                                                        </div>
                                                    </div>
                                                    <div className="flex-1">
                                                        <label className="text-sm">{t('Attribute Value')}</label>
                                                        <div className="mt-1 rounded-xl bg-default-100 px-4 py-2.5 text-sm text-default-700 break-words">
                                                            {formatMetadataValue(item.value)}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </>
                                    )}
                                </div>
                            </ModalBody>
                        </>
                    )}
                </ModalContent>
            </Modal>
        </>
    );
}
